import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { AIResponseSave } from "winslow-core";
import { getCategoryColor } from "./colorData";
import { toMMDD, convertUTCtoLocal } from "winslow-core";

ChartJS.register(...registerables);

// start and end need to be in PST
type CategoriesChartProps = {
  responses: AIResponseSave[];
  startDate: Date;
  endDate: Date;
  period?: "daily" | "monthly";
};

export const CategoriesChart = ({
  responses,
  startDate,
  endDate,
  period = "daily",
}: CategoriesChartProps) => {
  const getBucket = (forDate: Date): number => {
    const daysApart = calculateDaysBetweenDates(startDate, forDate);
    return daysApart;
  };

  function calculateDaysBetweenDates(date1: Date, date2: Date) {
    // Convert both dates to milliseconds
    const date1Millis = date1.getTime();
    const date2Millis = date2.getTime();

    // Calculate the difference in milliseconds
    const differenceMillis = date2Millis - date1Millis;

    // Convert milliseconds to days
    const differenceDays = differenceMillis / (1000 * 60 * 60 * 24);

    // Round to handle any daylight saving time issues
    const diffDays = Math.round(differenceDays);

    return diffDays;
  }

  const getLabelsForDateRange = () => {
    const rangeDays = calculateDaysBetweenDates(startDate, endDate);
    const labels: string[] = [];

    for (let i = 0; i <= rangeDays; i++) {
      const newDate = new Date(startDate.getTime());
      newDate.setDate(startDate.getDate() + i);
      labels.push(toMMDD(newDate));
    }

    return labels;
  };

  const getSeriesDataForCategory = (
    buckets: Map<number, AIResponseSave[]>,
    category: string
  ) => {
    const rangeDays = calculateDaysBetweenDates(startDate, endDate);
    const data: number[] = [];

    for (let i = 0; i <= rangeDays; i++) {
      const responses = buckets.get(i);

      if (responses) {
        let count = 0;
        responses.forEach((response: AIResponseSave) => {
          if (response.category === category) {
            count++;
          }
        });
        data.push(count);
      } else {
        data.push(0);
      }
    }

    return data;
  };

  const getDataSets = (buckets: Map<number, AIResponseSave[]>) => {
    // 1. find all the categories
    const categoryMap: Map<string, boolean> = new Map<string, boolean>();

    responses.forEach((response: AIResponseSave) => {
      categoryMap.set(response.category, true);
    });

    const categories = Array.from(categoryMap.keys());

    const datasets: any[] = [];

    categories.forEach((category: string, i: number) => {
      const dataset = {
        label: category,
        data: getSeriesDataForCategory(buckets, category),
        fill: false,
        backgroundColor: getCategoryColor(category),
        borderColor: getCategoryColor(category),
      };

      datasets.push(dataset);
    });

    return datasets;
  };

  // sort by date oldest to newest
  responses.sort((a: AIResponseSave, b: AIResponseSave) => {
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  // put all the responses into their buckets
  const buckets: Map<number, AIResponseSave[]> = new Map<
    number,
    AIResponseSave[]
  >();

  responses.forEach((response: AIResponseSave, i: number) => {
    const responseDate = convertUTCtoLocal(new Date(response.createdAt));
    const bucket = getBucket(responseDate);

    console.log("Response date:", responseDate, "Bucket:", bucket);

    if (!buckets.has(bucket)) {
      buckets.set(bucket, [] as AIResponseSave[]);
    }

    const bucketResponses = buckets.get(bucket);
    bucketResponses?.push(response);

    if (bucketResponses) {
      buckets.set(bucket, bucketResponses);
    }
  });

  const data = {
    labels: getLabelsForDateRange(),
    datasets: getDataSets(buckets),
    // datasets: [
    //     {
    //         label: 'My First Dataset',
    //         data: [65, 59, 80, 81, 56, 55, 40],
    //         fill: false,
    //         backgroundColor: 'rgba(75,192,192,0.2)',
    //         borderColor: 'rgba(75,192,192,1)',
    //     },
    //     {
    //         label: 'Dataset 2',
    //         data: [35, 39, 60, 51, 36, 25, 20],
    //         backgroundColor: 'rgba(54, 162, 235, 0.2)',
    //         borderColor: 'rgba(54, 162, 235, 1)',
    //         borderWidth: 1,
    //     },
    // ],
  };

  // const options = {
  //     scales: {
  //         y: {
  //             beginAtZero: true,
  //         },
  //     },
  // };

  const options = {
    plugins: {
      title: {
        display: true,
        text: "Questions By Category L30",
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
      legend: {
        display: false,
        position: "right" as const,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        ticks: {
          autoSkip: false,
          maxRotation: 90 as const,
          minRotation: 90 as const,
        },
      },
      y: {
        stacked: true,
      },
    },
  };

  return (
    <>
      <Chart type="bar" height="300px" data={data} options={options} />
    </>
  );
};
